import React, { useEffect, useState, useMemo, useCallback } from "react";
import { message, Form, Input, Card, Collapse, Row, Col, Image, Typography, Popconfirm, Drawer, Tag, Skeleton } from "antd";
import { Dialog } from "evergreen-ui";
import { EditIcon, TrashIcon } from "evergreen-ui/esm/icons";
import Firebase from "../../Firebase/Firebase";
import { useObject } from "../../Firebase/databaseHooks";
import { useParams, useNavigate } from "react-router-dom";
import FireFetch from "../../Firebase/FireFetch";
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, SectionType, TextRun } from "docx";
import { getDatabase, ref, update, onValue } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, listAll } from "firebase/storage";
import AddLessonComponent from "../../Modals/Lessons/AddLessonComponent";
import AddLessonPage from "../../Modals/Lessons/AddLessonPage";
import { IconButton, Button, List } from "@mui/material";
import { typeFieldsMap } from "../../Modals/Lessons/AddLessonComponent";
import "./view.css";
import CaptionText from "../../Modals/CaptionText/Caption";
import EditLessonModal from "../../Modals/Lessons/EditLesson";
import { Add, Delete, Edit } from "@mui/icons-material";
import EditLessonComponent from "../../Modals/Lessons/EditLessonComponent";
import DuplicateLesson from "../../Modals/Lessons/DuplicateLesson";
import { useAuth } from "../../AuthContext/AuthContext";

const { Panel } = Collapse;

const storage = getStorage(Firebase);
const db = getDatabase(Firebase);

const LessonDetailsV2 = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { canAccessModule } = useAuth();
	const canCreateLesson = canAccessModule("LESSONS", "create");
	const canUpdateLesson = canAccessModule("LESSONS", "update");
	const canDeleteLesson = canAccessModule("LESSONS", "delete");
	const [form] = Form.useForm();
	// State management
	const [coreChecked, setCoreChecked] = useState(false);
	const [nextLessonName, setNextLessonName] = useState(null);
	const [filesList, setFilesList] = useState([]);
	const [showLoading, setShowLoading] = useState(false);
	const [lesson, setLesson] = useState(null);
	const [linkNum, setLinkNum] = useState(0);
	const [linkArray, setLinkArray] = useState([]);
	const [mediaLinks, setMediaLinks] = useState({});
	const [ownerDetails, setOwnerDetails] = useState(null);
	const [org, setOrg] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [openAddLesson, setOpenAddLesson] = useState(false);
	const [pageNumber, setPageNumber] = useState(0);
	const [selectedComponent, setSelectedComponent] = useState(null);
	const [openAddLessonPage, setOpenAddLessonPage] = useState(false);
	const [categories, setCategories] = useState([]);
	const [subCategories, setSubCategories] = useState([]);
	const [lessonLanguage, setLanguage] = useState();
	const [showDuplicateLessonModal, setShowDuplicateLessonModal] = useState(false);

	const [snapshot, loading, error] = useObject(ref(db, `talmidsdb/lessons/${id}`));
	const lessonData = useMemo(() => snapshot?.val(), [snapshot]);
	useEffect(() => {
		if (lessonData?.nextLesson) {
			const nextLessonRef = ref(db, `talmidsdb/lessons/${lessonData.nextLesson}/lessonName`);
			onValue(nextLessonRef, (snapshot) => {
				setNextLessonName(snapshot.val());
			});
		} else {
			setNextLessonName(null);
		}
	}, [lessonData?.nextLesson]);

	const fetchRelatedData = useCallback(async (lessonData) => {
		if (!lessonData) return;

		if (lessonData?.assignedTo) {
			const ownerRef = ref(db, `talmidsdb/users/${lessonData.assignedTo}`);
			onValue(ownerRef, (snapshot) => {
				setOwnerDetails(snapshot.val());
			});
		}

		if (lessonData?.language) {
			const lang = ref(db, `talmidsdb/languages/${lessonData.language}`);
			onValue(lang, (snapshot) => {
				setLanguage(snapshot.val());
			});
		}

		if (lessonData.organisation) {
			const orgRef = ref(db, `talmidsdb/organisations/${lessonData.organisation}`);
			onValue(orgRef, (snapshot) => {
				setOrg(snapshot.val());
			});
		}

		if (lessonData.categories?.length) {
			const categoryPromises = lessonData.categories.map(categoryId => {
				return new Promise((resolve) => {
					onValue(ref(db, `talmidsdb/categories/${categoryId}`), (snapshot) => {
						resolve(snapshot.val());
					}, { onlyOnce: true });
				});
			});
			const categoryData = await Promise.all(categoryPromises);
			setCategories(categoryData.filter(Boolean));
		}

		if (lessonData.subCategories?.length) {
			const subCategoryPromises = lessonData.subCategories.map(categoryId => {
				return new Promise((resolve) => {
					onValue(ref(db, `talmidsdb/subCategories/${categoryId}`), (snapshot) => {
						resolve(snapshot.val());
					}, { onlyOnce: true });
				});
			});
			const subCategoryData = await Promise.all(subCategoryPromises);
			setSubCategories(subCategoryData.filter(Boolean));
		}

		if (lessonData.videoLinks?.length) {
			const arry = lessonData.videoLinks.map((link, i) => (
				<div className="my-3 ml-3" key={i}>
					<Form.Item label={`Link - ${i + 1}`}>
						<Input
							placeholder={`Enter Video Title ${i + 1}`}
							defaultValue={link.title}
							id={`videoTitle-${i + 1}`}
						/>
						<Input
							placeholder={`Enter Video Link ${i + 1}`}
							defaultValue={link.link}
							id={`videoLink-${i + 1}`}
						/>
					</Form.Item>
				</div>
			));
			setLinkArray(arry);
			setLinkNum(lessonData.videoLinks.length);
		}

		try {
			const object = {};
			const res = await listAll(storageRef(storage, `System/Lessons/${id}`));
			const urlPromises = res.items.map(itemRef =>
				getDownloadURL(itemRef).then(url => {
					object[itemRef.name] = url;
				})
			);

			await Promise.all(urlPromises);
			setMediaLinks(object);
		} catch (error) {
			console.error("Error fetching media links:", error);
		}
	}, [id]);

	useEffect(() => {
		if (lessonData) {
			setLesson(lessonData);
			setCoreChecked(lessonData.coreLesson ?? false);
			fetchRelatedData(lessonData);
		}
	}, [lessonData, fetchRelatedData]);

	const handleFileUpload = useCallback((e) => {
		const fileObject = {
			fileID: e.target.id,
			file: e.target.files[0]
		};

		setFilesList(prevFiles => {
			const index = prevFiles.findIndex(x => x.fileID === fileObject.fileID);
			if (index !== -1) {
				const newFiles = [...prevFiles];
				newFiles[index] = fileObject;
				return newFiles;
			}
			return [...prevFiles, fileObject];
		});
	}, []);

	const addFile = useCallback((fileObject) => {
		return uploadBytes(storageRef(storage, `talmidsdb/Lessons/${id}/${fileObject.fileID}`), fileObject.file);
	}, [id]);

	const deleteLesson = useCallback(async (lessonID) => {
		if (!canDeleteLesson) {
			message.error("You do not have permission to delete lessons.");
			return;
		}

		try {
			const folderRef = storageRef(storage, `System/Lessons/${lessonID}`);
			const res = await listAll(folderRef);
			const deletePromises = res.items.map(itemRef => {
				if (typeof itemRef.delete === 'function') {
					return itemRef.delete().catch(err => {
						console.error(`Failed to delete file ${itemRef.fullPath}:`, err);
						return Promise.resolve();
					});
				} else {
					console.error(`Invalid itemRef - delete not a function:`, itemRef);
					return Promise.resolve();
				}
			});

			await Promise.all(deletePromises);

			const result = await FireFetch.DeleteFromDB("lessons", lessonID);

			if (result === "success") {
				await FireFetch.addLog("lessons", lessonID, "Delete");
				message.success("Lesson deleted successfully");
				navigate("/lessons");
			} else {
				message.error("Unable to delete lesson, an error occurred: " + result);
			}
		} catch (err) {
			console.error("Unexpected error during deletion process:", err);
			message.error("Unexpected error occurred. Please try again.");
		}
	}, [navigate, canDeleteLesson]);

	const handleEditLesson = useCallback(async (values) => {
		if (!canUpdateLesson) {
			message.error("You do not have permission to update lessons.");
			return;
		}

		const lessonObject = { ...values };
		const emptyArray = [];

		linkArray.forEach((_, index) => {
			const linked = document.getElementById(`videoLink-${index + 1}`)?.value;
			const title = document.getElementById(`videoTitle-${index + 1}`)?.value;

			if (linked || title) {
				emptyArray.push({
					title: title || "",
					link: linked || ""
				});
			}
		});

		lessonObject.videoLinks = emptyArray;

		// Clean undefined values
		Object.keys(lessonObject).forEach(key => {
			if (lessonObject[key] === undefined) {
				lessonObject[key] = "";
			}
		});

		setShowLoading(true);

		try {
			await Promise.all(filesList.map(addFile));
			await update(ref(db, `talmidsdb/lessons/${id}`), lessonObject);
			await FireFetch.addLog("lessons", id, "Edit");
			message.success("Lesson updated successfully");
			navigate("/lessons");
		} catch (error) {
			console.error("Error updating lesson:", error);
			message.error("Unable to update lesson due to an error: " + error.message);
		} finally {
			setShowLoading(false);
		}
	}, [linkArray, filesList, id, navigate, addFile, canUpdateLesson]);

	const printLesson = useCallback(() => {
		if (!lesson) return;

		const doc = new Document({
			styles: {
				paragraphStyles: [{
					id: "wellSpaced",
					name: "Well Spaced",
					basedOn: "Normal",
					quickFormat: true,
					paragraph: {
						spacing: { line: 276, before: 20 * 72 * 0.1, after: 20 * 72 * 0.05 },
					},
				}],
			},
			sections: Object.entries(lesson).map(([key, value]) => ({
				properties: { type: SectionType.CONTINUOUS },
				children: [
					new Paragraph({
						children: [new TextRun({ text: key, bold: true })]
					}),
					new Paragraph({
						children: [new TextRun({
							text: Array.isArray(value) ? value.join(", ") :
								(value === null || value === undefined) ? "-" :
									value.toString()
						})]
					}),
					new Paragraph({ children: [new TextRun({ text: " " })] }),
				],
			}))
		});

		Packer.toBlob(doc).then((blob) => {
			saveAs(blob, "LessonDetails.docx");
		});
	}, [lesson]);

	const deleteComponent = useCallback(async (pageIndex, componentIndex) => {
		if (!canUpdateLesson) {
			message.error("You do not have permission to update lessons.");
			return;
		}

		try {
			const updatedPages = [...lesson.pages];
			updatedPages[pageIndex].components.splice(componentIndex, 1);

			await update(ref(db, `talmidsdb/lessons/${id}`), {
				pages: updatedPages
			});

			message.success("Component deleted successfully");
		} catch (error) {
			console.error("Error deleting component:", error);
			message.error("Failed to delete component");
		}
	}, [lesson, id, canUpdateLesson]);

	const deletePage = useCallback(async (pageIndex) => {
		if (!canUpdateLesson) {
			message.error("You do not have permission to update lessons.");
			return;
		}

		if (!lesson?.pages?.[pageIndex]) return;

		try {
			const page = lesson.pages[pageIndex];

			// Delete associated files
			if (page.components) {
				await Promise.all(
					page.components.flatMap(component =>
						(typeFieldsMap[component.type] || [])
							.filter(field => field.isFile && component[field.name])
							.map(async field => {
								try {
									const fileRef = storageRef(storage, component[field.name]);
									await fileRef.delete();
								} catch (err) {
									console.warn(`Could not delete file ${component[field.name]}:`, err);
								}
							})
					)
				);
			}

			// Update pages
			const updatedPages = lesson.pages
				.filter((_, index) => index !== pageIndex)
				.map((page, index) => ({ ...page, pageOrder: index }));

			await update(ref(db, `talmidsdb/lessons/${id}`), {
				pages: updatedPages
			});

			message.success("Page and its components deleted successfully");
			setSelectedComponent(null);
		} catch (error) {
			console.error("Error deleting page:", error);
			message.error("Failed to delete page");
		}
	}, [lesson, id, canUpdateLesson]);

	// Utility functions
	const ensureValidFirebaseUrl = useCallback((url) => {
		if (!url) return null;
		if (url.includes('firebasestorage.googleapis.com') || url?.startsWith('http') || url?.startsWith('https')) {
			return url;
		}
		if (url?.startsWith('gs://')) {
			const path = url.replace('gs://', '').split('/').slice(1).join('/');
			return `https://firebasestorage.googleapis.com/v0/b/${path}`;
		}
		return null;
	}, []);

	const getFileType = useCallback((fieldName, url) => {
		if (fieldName === 'audioFile') return 'audio';
		if (fieldName === 'animationPath') return 'animation';
		if (fieldName === 'activity') return 'activity';
		if (fieldName.includes('image') || fieldName.includes('Image')) return 'image';
		if (url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return 'image';
		if (url?.match(/\.(mp3|wav|ogg|m4a)$/i)) return 'audio';
		if (url?.match(/\.(mp4|mov|avi|webm)$/i)) return 'video';
		return 'unknown';
	}, []);

	const getAudioType = useCallback((url) => {
		if (url?.includes('.mp3')) return 'mpeg';
		if (url?.includes('.wav')) return 'wav';
		if (url?.includes('.ogg')) return 'ogg';
		if (url?.includes('.m4a')) return 'mp4';
		return 'mpeg';
	}, []);

	if (loading) return <LessonDetailsSkeleton />;
	if (error) return <div>Error loading lesson: {error.message}</div>;
	if (!lesson) return <div>No lesson data found</div>;

	return (
		<div className="d-flex justify-content-center align-items-center pt-4 mt-4">
			<div className="p-2 px-3 w-100">
				<Drawer
					title="Edit Lesson"
					width={500}
					maskClosable
					className="font-Nunito"
					placement="right"
					onClose={() => setShowModal(false)}
					visible={showModal}
				>
					<EditLessonModal modal={setShowModal} />
				</Drawer>
				<Dialog
					isShown={openAddLessonPage}
					title="Add Page"
					onCloseComplete={() => setOpenAddLessonPage(false)}
					confirmLabel="Add"
					className="font-Nunito"
					hasFooter={false}
					shouldCloseOnOverlayClick={false}
				>
					<AddLessonPage id={id} snapshot={snapshot} OpenAddLessonPage={setOpenAddLessonPage} />
				</Dialog>
				<Dialog
					isShown={showDuplicateLessonModal}
					title="Duplicate Lesson"
					onCloseComplete={() => setShowDuplicateLessonModal(false)}
					confirmLabel="Add"
					className="font-Nunito"
					hasFooter={false}
					shouldCloseOnOverlayClick={false}
				>
					<DuplicateLesson
						id={id}
						snapshot={snapshot}
						onDuplicateComplete={(newLessonId) => {
							navigate(`/lessons/${newLessonId}`);
							setShowDuplicateLessonModal(false);
						}}
					/>
				</Dialog>
				<Dialog
					isShown={openAddLesson}
					title="Add Component"
					onCloseComplete={() => setOpenAddLesson(false)}
					confirmLabel="Add"
					className="font-Nunito"
					hasFooter={false}
					shouldCloseOnOverlayClick={false}
				>
					<AddLessonComponent id={id} snapshot={snapshot} pageTo={pageNumber} />
				</Dialog>

				<Form layout="vertical" form={form}>
					<Row gutter={[4, 4]}>
						<Col span={8}>
							<LessonInfoCard
								lesson={lesson}
								categories={categories}
								subCategories={subCategories}
								lessonLanguage={lessonLanguage}
								nextLessonName={nextLessonName}
								ownerDetails={ownerDetails}
								onEdit={() => setShowModal(true)}
								onDelete={() => deleteLesson(id)}
								onPrint={printLesson}
								onPublish={() => {
									if (!canUpdateLesson) {
										message.error("You do not have permission to update lessons.");
										return;
									}

									if (lesson.lessonStatus !== 'published') {
										update(ref(db, `talmidsdb/lessons/${id}`), { lessonStatus: "published" })
									} else {
										update(ref(db, `talmidsdb/lessons/${id}`), { lessonStatus: "draft" })
									}
								}}
								hasPages={lesson.pages?.length > 0}
								onDuplicateLesson={() => setShowDuplicateLessonModal(true)}
								canCreateLesson={canCreateLesson}
								canUpdateLesson={canUpdateLesson}
								canDeleteLesson={canDeleteLesson}
							/>
						</Col>
						<Col span={16}>
							<PagesSection
								lesson={lesson}
								onAddPage={() => setOpenAddLessonPage(true)}
								onAddComponent={(pageOrder) => {
									setPageNumber(pageOrder);
									setOpenAddLesson(true);
								}}
								onDeletePage={deletePage}
								onDeleteComponent={deleteComponent}
								selectedComponent={selectedComponent}
								onSelectComponent={setSelectedComponent}
								ensureValidFirebaseUrl={ensureValidFirebaseUrl}
								getFileType={getFileType}
								getAudioType={getAudioType}
								navigate={navigate}
								canUpdateLesson={canUpdateLesson}
							/>
						</Col>
					</Row>
				</Form>
			</div>
		</div>
	);
};

const LessonDetailsSkeleton = () => (
	<div className="d-flex justify-content-center align-items-center pt-4 mt-4">
		<div className="p-2 px-3 w-100">
			<Row gutter={[4, 4]}>
				<Col span={8}>
					<Card style={{ borderRadius: 10 }}>
						<Skeleton active avatar paragraph={{ rows: 12 }} />
					</Card>
				</Col>
				<Col span={16}>
					<PageComponentsSkeleton />
				</Col>
			</Row>
		</div>
	</div>
);

const PageComponentsSkeleton = () => (
	<Card style={{ borderRadius: 10 }}>
		<Row gutter={[8, 8]}>
			<Col span={9}>
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
					<Skeleton.Button active style={{ width: 110 }} />
				</div>
				{[0, 1, 2].map((item) => (
					<Card key={item} className="mb-3">
						<Skeleton active title={{ width: '65%' }} paragraph={{ rows: 1 }} />
					</Card>
				))}
			</Col>
			<Col span={14}>
				<Card>
					<Skeleton active title={{ width: '45%' }} paragraph={{ rows: 9 }} />
				</Card>
			</Col>
		</Row>
	</Card>
);

const LessonInfoCard = ({
	lesson,
	categories,
	subCategories,
	ownerDetails,
	lessonLanguage,
	onEdit,
	onDuplicateLesson,
	onDelete,
	onPublish,
	hasPages,
	nextLessonName,
	canCreateLesson,
	canUpdateLesson,
	canDeleteLesson,
}) => {
	return (
		<Card style={{ borderRadius: 10 }}>
			<Row gutter={[3, 3]}>
				<Col span={12}>
					<p className="h6 py-4">{lesson?.lessonName ?? '-'}</p>
					<Image width={200} src={lesson?.lessonImage} alt="Lesson thumbnail" />
				</Col>
				<Col span={12}>
					<CaptionText caption="Status" textValue={<Tag color={lesson?.lessonStatus === 'published' ? 'green' : 'warning'}>{lesson?.lessonStatus ?? '-'}</Tag>} />
					<CaptionText caption="Lesson Name" textValue={lesson?.lessonName ?? '-'} />
					<CaptionText caption="Theme" textValue={lesson?.lessonTheme ?? '-'} />
					<CaptionText caption="Code" textValue={lesson?.lessonCode ?? '-'} />
					<CaptionText caption="Langauge" textValue={lessonLanguage?.languageName ?? '-'} />
					<CaptionText
						caption="Category"
						textValue={categories?.map(c => c?.name).join(", ") || "No categories selected"}
					/>
					<CaptionText
						caption="Sub Category"
						textValue={subCategories?.map(c => c?.categoryName).join(", ") || "No subcategories selected"}
					/>
					<CaptionText caption="Description" textValue={lesson?.lessonDescription ?? '-'} />
					<CaptionText caption="Difficulty" textValue={lesson?.lessonDifficulty ?? '-'} />
					<CaptionText caption="Materials Needed" textValue={lesson?.materialsNeeded ?? '-'} />
					<CaptionText caption="Owner" textValue={lesson?.owner ?? '-'} />
					<CaptionText
						caption="Assigned To"
						textValue={`${ownerDetails?.firstname ?? ''} ${ownerDetails?.surname ?? ''}`.trim() || '-'}
					/>
					<CaptionText
						caption="Next Lesson"
						textValue={nextLessonName ?? '-'} />
				</Col>
			</Row>
			<div className="flex justify-between items-center mt-3">
				<div className="flex gap-2">
					{canUpdateLesson && (
						<Button intent="none" onClick={onEdit} iconBefore={EditIcon}>
							Edit
						</Button>
					)}
					{canCreateLesson && (
						<Button intent="none" onClick={onDuplicateLesson} iconBefore={EditIcon}>
							Duplicate
						</Button>
					)}
					{canDeleteLesson && (
						<Popconfirm
							title="Are you sure you want to delete this lesson?"
							onConfirm={onDelete}
							okText="Yes"
							cancelText="No"
							color="red"
						>
							<Button intent="danger" iconBefore={TrashIcon}>
								Delete
							</Button>
						</Popconfirm>
					)}
				</div>
				<div>
					{canUpdateLesson && (
						<Popconfirm
							title={hasPages ? `Are you sure you want to ${lesson.lessonStatus === 'published' ? 'Unpublish' : 'Publish'} this lesson?` : "You cannot publish a lesson without pages."}
							onConfirm={hasPages ? onPublish : undefined}
							okText="Yes"
							cancelText="No"
							color={hasPages ? "green" : "grey"}
						>
							<Button disabled={!hasPages} intent="none" variant="outlined" color="success">
								{lesson.lessonStatus === 'published' ? 'Unpublish' : 'Publish'}
							</Button>
						</Popconfirm>
					)}
				</div>
			</div>
		</Card>
	)
};

const PagesSection = ({
	lesson,
	onAddPage,
	onAddComponent,
	onDeletePage,
	onDeleteComponent,
	selectedComponent,
	onSelectComponent,
	ensureValidFirebaseUrl,
	getFileType,
	getAudioType,
	navigate,
	canUpdateLesson
}) => {
	const [editingComponent, setEditingComponent] = useState(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [componentIndex, setComponentIndex] = useState(0);

	const handleEditClick = (component, pageIndex, compIndex) => {
		setEditingComponent(component);
		setCurrentPage(pageIndex);
		setComponentIndex(compIndex);
	};

	return (
		<Card style={{ borderRadius: 10 }}>
			<Row gutter={[8, 8]}>
				<Col span={9}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
						{canUpdateLesson && (
							<Button onClick={onAddPage} style={{ background: "whitesmoke" }}>
								<Typography className="font-semibold">Add Page</Typography>
							</Button>
						)}
					</div>
					<Collapse accordion>
						{lesson?.pages?.map((page, pageIndex) => (
							<Panel
								header={
									<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
										<div>{`${(page?.pageOrder ?? pageIndex) + 1}. ${page?.pageName ?? 'Untitled Page'}`}</div>
										{canUpdateLesson && (
											<div style={{ display: 'flex', gap: '8px' }}>
												<IconButton
													style={{ backgroundColor: "transparent" }}
													onClick={(e) => {
														e.stopPropagation();
														onAddComponent(page?.pageOrder);
													}}
												>
													<Add />
												</IconButton>
												<Popconfirm
													title="Are you sure you want to delete this page and all its components?"
													onConfirm={(e) => {
														e.stopPropagation();
														onDeletePage(pageIndex);
													}}
													onCancel={(e) => e.stopPropagation()}
													okText="Yes"
													cancelText="No"
												>
													<IconButton
														style={{ backgroundColor: "transparent" }}
														onClick={(e) => e.stopPropagation()}
													>
														<Delete color="error" />
													</IconButton>
												</Popconfirm>
											</div>
										)}
									</div>
								}
								key={pageIndex}
							>
								{page?.components?.map((component, compIndex) => (
									<React.Fragment key={compIndex}>
										<ComponentCard
											component={component}
											onClick={() => onSelectComponent(component)}
											onEdit={() => handleEditClick(component, pageIndex, compIndex)}
											onDelete={(e) => {
												e.stopPropagation();
												onDeleteComponent(pageIndex, compIndex);
											}}
											canUpdateLesson={canUpdateLesson}
										/>
									</React.Fragment>
								))}
							</Panel>
						))}
					</Collapse>
				</Col>
				<Col span={14}>
					{editingComponent ? (
						<EditLessonComponent
							lessonId={lesson.id}
							lessonData={lesson}
							pageIndex={currentPage}
							componentData={editingComponent}
							componentIndex={componentIndex}
							onSuccess={() => {
								setEditingComponent(null);
								onSelectComponent(null);
							}}
							onCancel={() => setEditingComponent(null)}
						/>
					) : (
						<ComponentPreview
							selectedComponent={selectedComponent}
							ensureValidFirebaseUrl={ensureValidFirebaseUrl}
							getFileType={getFileType}
							getAudioType={getAudioType}
							navigate={navigate}
						/>
					)}
				</Col>
			</Row>
		</Card>
	);
};

const ComponentCard = ({ component, onClick, onEdit, onDelete, canUpdateLesson }) => (
	<Card hoverable className="mb-3" onClick={onClick}>
		<p><strong>Component:</strong> {component.type || 'Unnamed'}</p>
		<p>{(component?.header ?? component?.target ?? component?.title) || 'No content available'}</p>
		{canUpdateLesson && (
			<div style={{ position: 'absolute', bottom: '8px', right: '8px' }}>
				<Edit
					style={{ cursor: 'pointer', fontSize: '18px', marginRight: '8px' }}
					onClick={(e) => {
						e.stopPropagation();
						onEdit();
					}}
				/>
				<Popconfirm
					title="Are you sure you want to delete this component?"
					onConfirm={onDelete}
					okText="Yes"
					cancelText="No"
				>
					<Delete
						style={{ color: 'red', cursor: 'pointer', fontSize: '18px' }}
						onClick={(e) => e.stopPropagation()}
					/>
				</Popconfirm>
			</div>
		)}
	</Card>
);

const ComponentPreview = ({
	selectedComponent,
	ensureValidFirebaseUrl,
	getFileType,
	getAudioType,
	navigate
}) => (
	<div className="flex gap-3">
		<Col span={14}>
			<div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[700px] w-[350px] shadow-xl">
				<div className="w-[148px] h-[18px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>
				<div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
				<div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
				<div className="h-[64px] w-[3px] bg-gray-800 absolute -end-[17px] top-[142px] rounded-e-lg"></div>
				<Card>
					{selectedComponent ? (
						<ComponentDetails
							component={selectedComponent}
							ensureValidFirebaseUrl={ensureValidFirebaseUrl}
							getFileType={getFileType}
							getAudioType={getAudioType}
							navigate={navigate}
						/>
					) : (
						<p className="text-gray-500 text-center">Select a component to preview it here</p>
					)}
				</Card>
			</div>
		</Col>
	</div>
);

const ComponentDetails = ({ component, ensureValidFirebaseUrl, getFileType, getAudioType, navigate }) => (
	<>
		{(typeFieldsMap[component?.type] || []).map((field, index) => {
			const value = component[field.name];
			const isFileField = field.isFile && value && typeof value === 'string';

			if (component.type === 'title') {
				return (
					<React.Fragment key={index}>
						{field.name === 'title' && (
							<div className="mb-2">
								<p>Title</p>
								<p className="font-bold underline">{value}</p>
							</div>
						)}
					</React.Fragment>
				);
			}

			if (component.type === 'link') {
				return (
					<React.Fragment key={index}>
						{field.name === 'header' && (
							<FieldPreview label={field.label} value={value} />
						)}
						{field.name === 'linkDescription' && (
							<FieldPreview label={field.label} value={value} />
						)}
						{field.name === 'links' && component?.links && (
							<div className="mb-2">
								<p className="font-semibold">Links</p>
								<div className="space-y-1">
									{component.links.map((link, idx) => (
										<div key={idx} className="flex items-center">
											<a
												href={link?.linkPath?.startsWith('http') ? link.linkPath : `https://${link.linkPath}`}
												target="_blank"
												rel="noopener noreferrer"
												className="text-blue-600 hover:underline break-all"
											>
												<span style={{ textDecoration: 'none', color: 'black' }}>
													{link.linkTitle}
												</span>
												<br />
												{link.linkPath}
											</a>
										</div>
									))}
								</div>
							</div>
						)}
					</React.Fragment>
				);
			}

			if (isFileField) {
				return (
					<FilePreview
						key={index}
						field={field}
						value={value}
						ensureValidFirebaseUrl={ensureValidFirebaseUrl}
						getFileType={getFileType}
						getAudioType={getAudioType}
					/>
				);
			}

			if (component.type === 'activity') {
				return (
					<div key={index} className="mb-2">
						<p className="font-semibold">{field.label}</p>
						<Button
							onClick={() => component.activityTarget && navigate(component.activityTarget)}
							variant="outlined"
							style={{
								color: "#51195D",
								borderRadius: 15,
								padding: '0 2rem',
								borderColor: "#51195D",
								textTransform: 'none',
								fontSize: '0.7rem',
								fontWeight: 'normal',
								minWidth: '200px',
								height: '40px'
							}}
						>
							{value || 'Complete activity'}
						</Button>
					</div>
				);
			}

			return <FieldPreview key={index} label={field.label} value={value} />;
		})}
	</>
);

const FieldPreview = ({ label, value }) => (
	<div className="mb-2">
		<p className="font-semibold">{label}</p>
		<p className="text-gray-700">{value ?? '—'}</p>
	</div>
);

const FilePreview = ({ field, value, ensureValidFirebaseUrl, getFileType, getAudioType }) => {
	const downloadUrl = ensureValidFirebaseUrl(value);
	const fileType = getFileType(field.name, value);

	if (!downloadUrl) {
		return (
			<div className="mb-2">
				<p className="font-semibold">{field.label}</p>
				<p className="text-red-500">Invalid file URL</p>
			</div>
		);
	}

	switch (fileType) {
		case 'image':
			return (
				<div className="mb-2">
					<p className="font-semibold">{field.label}</p>
					<img
						src={downloadUrl}
						alt={field.label}
						className="w-full rounded"
						onError={(e) => e.target.style.display = 'none'}
					/>
				</div>
			);
		case 'audio':
			return (
				<div className="mb-2">
					<p className="font-semibold">{field.label}</p>
					<audio controls className="w-full">
						<source src={downloadUrl} type={`audio/${getAudioType(value)}`} />
						Your browser does not support audio element.
					</audio>
				</div>
			);
		case 'animation':
		case 'video':
			return (
				<div className="mb-2">
					<p className="font-semibold">{field.label}</p>
					<video controls className="w-full rounded-md">
						<source src={downloadUrl} type="video/mp4" />
						Your browser does not support video element.
					</video>
				</div>
			);
		default:
			return (
				<div className="mb-2">
					<p className="font-semibold">{field.label}</p>
					<a
						href={downloadUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="text-blue-600 hover:underline"
					>
						Download File
					</a>
				</div>
			);
	}
};

export default LessonDetailsV2;
